import { useEffect, useRef } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";

import {
  HostedPairingRouteSurface,
  PairingPendingSurface,
  PairingRouteSurface,
} from "../components/auth/PairingRouteSurface";
import { useMorrowClerkAuth } from "../components/clerk/morrowClerkAuth";
import { hasClerkPublishableKey } from "../cloud/publicConfig";

/**
 * A hosted web deploy authenticates through Clerk, not pairing tokens. When
 * Clerk is configured and this is a browser (not the desktop shell), show a
 * Clerk sign-in surface here instead of the pairing UI.
 */
function isHostedClerkWeb(): boolean {
  return (
    hasClerkPublishableKey() && typeof window !== "undefined" && window.desktopBridge === undefined
  );
}

function HostedClerkSignInSurface() {
  const { isLoaded, isSignedIn, openSignIn } = useMorrowClerkAuth();
  const openedRef = useRef(false);

  // Once signed in, the backend session can be minted from the Clerk token.
  // Reload so the auth bootstrap re-runs (now carrying the token) and lands in
  // the app rather than staying on this screen.
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      window.location.replace("/");
    }
  }, [isLoaded, isSignedIn]);

  // Skip the redundant intermediate "Sign in to continue" screen: a signed-out
  // hosted-web visitor should land straight in the Clerk sign-in, not on an extra
  // page with its own button. Auto-open once as soon as Clerk is ready. The
  // surface below stays only as a fallback (e.g. if the modal is dismissed).
  useEffect(() => {
    if (isLoaded && !isSignedIn && !openedRef.current) {
      openedRef.current = true;
      openSignIn();
    }
  }, [isLoaded, isSignedIn, openSignIn]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 px-6 text-center">
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">MorrowOS</h1>
        <p className="text-sm text-muted-foreground">Sign in to continue.</p>
      </div>
      <button
        type="button"
        onClick={() => openSignIn()}
        disabled={!isLoaded}
        className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
      >
        {isLoaded ? "Sign in" : "Loading…"}
      </button>
    </div>
  );
}

export const Route = createFileRoute("/pair")({
  beforeLoad: async ({ context }) => {
    const { authGateState } = context;
    if (authGateState.status === "hosted-pairing") {
      return {
        authGateState,
      };
    }

    if (authGateState.status === "authenticated" || authGateState.status === "hosted-static") {
      throw redirect({ to: "/", replace: true });
    }
    return {
      authGateState,
    };
  },
  component: PairRouteView,
  pendingComponent: PairRoutePendingView,
});

function PairRouteView() {
  const { authGateState } = Route.useRouteContext();
  const navigate = useNavigate();

  if (!authGateState) {
    return null;
  }

  if (authGateState.status === "hosted-pairing") {
    return <HostedPairingRouteSurface />;
  }

  // Hosted web signs in with Clerk; the pairing-token surface is for
  // desktop-managed / self-hosted environments only.
  if (isHostedClerkWeb()) {
    return <HostedClerkSignInSurface />;
  }

  return (
    <PairingRouteSurface
      auth={authGateState.auth}
      onAuthenticated={() => {
        void navigate({ to: "/", replace: true });
      }}
      {...(authGateState.errorMessage ? { initialErrorMessage: authGateState.errorMessage } : {})}
    />
  );
}

function PairRoutePendingView() {
  return <PairingPendingSurface />;
}
